"use client";

/**
 * useEnableTrading - Hook para habilitar trading via Gnosis Safe
 *
 * Flujo Safe (gasless):
 * 1. Usuario hace click en "Enable Trading"
 * 2. Frontend: Crea embedded wallet si no existe (Privy SDK)
 * 3. Frontend: Registra session signer (permite al server firmar)
 * 4. Frontend: Deploy Safe + batch approvals via Polymarket relayer (gasless)
 * 5. Frontend: Crea CLOB credentials (signatureType=2)
 * 6. Backend: Almacena safe_address + credentials en DB
 *
 * Arquitectura:
 * - Embedded Wallet (EOA): Firma orders. Session signer para copytrading server-side.
 * - Safe Wallet: Holds funds. Polymarket paga gas para deploy + approvals.
 * - signatureType=2 (GNOSIS_SAFE): Orders firmadas por EOA, ejecutadas desde Safe.
 */

import { useState, useCallback, useEffect, useRef } from "react";
import {
  usePrivy,
  useWallets,
  useCreateWallet,
  useSigners,
  getAccessToken,
} from "@privy-io/react-auth";
import { useWallets as useSolanaWallets } from "@privy-io/react-auth/solana";
import { usePolymarketBalanceContext } from "@/contexts/PolymarketBalanceContext";
import { getCachedTradingStatus, invalidateTradingStatusCache } from "@/lib/trading-status-cache";
import { useSafeWallet } from "@/hooks/useSafeWallet";
import { useDepositWallet } from "@/hooks/useDepositWallet";
import { useInvalidateUserMe } from "@/hooks/useUserMeQuery";

/**
 * Deposit wallet feature flag (Polymarket Phase 1 — 2026-05-04+).
 * When ON, brand-new users (no existing safe_address) get a deposit wallet
 * instead of a Gnosis Safe. Existing Safe users always keep their flow —
 * the discriminator is purely "has the user already been onboarded with a
 * Safe?". Set NEXT_PUBLIC_USE_DEPOSIT_WALLET=true to opt in.
 */
const USE_DEPOSIT_WALLET =
  process.env.NEXT_PUBLIC_USE_DEPOSIT_WALLET === "true";

// Authorization key ID and policy IDs fetched from backend
// These are stored securely on the server and fetched at runtime
interface PrivyConfig {
  authorizationKeyId: string;
  policyId: string;         // EVM policy (Polygon + BSC)
  solanaPolicyId?: string;  // Solana (DFlow) policy
}

let cachedConfig: PrivyConfig | null = null;

async function getPrivyConfig(): Promise<PrivyConfig> {
  if (cachedConfig) return cachedConfig;

  const response = await fetch("/api/config/privy");
  if (!response.ok) {
    throw new Error("Failed to fetch Privy config");
  }
  const data = await response.json();
  cachedConfig = {
    authorizationKeyId: data.authorizationKeyId,
    policyId: data.policyId,
    solanaPolicyId: data.solanaPolicyId,
  };
  return cachedConfig;
}

export interface EnableTradingState {
  // Estado
  isLoading: boolean;
  isEnabled: boolean;
  isCheckingBackend: boolean; // True when forceBackendCheck is used and we're still checking
  needsRetry: boolean; // Has wallet but no credentials (incomplete setup)
  needsSafeUpgrade: boolean; // Has trading enabled but no Safe wallet (legacy EOA user)
  error: string | null;
  step: "idle" | "creating_wallet" | "registering_signer" | "enabling" | "done" | "error";
  stepMessage: string | null;

  // Wallet info
  walletAddress: string | null;

  // Metodos
  enableTrading: () => Promise<{ success: boolean; error?: string; depositWalletAddress?: string; safeAddress?: string }>;
  checkTradingStatus: () => Promise<boolean>;
  reset: () => void;
}

export function useEnableTrading(options?: { forceBackendCheck?: boolean }): EnableTradingState {
  const { ready, authenticated } = usePrivy();
  const { wallets } = useWallets();
  const { wallets: solanaWallets } = useSolanaWallets();
  const { createWallet } = useCreateWallet();
  const { addSigners, removeSigners } = useSigners();
  const { enableViaSafe } = useSafeWallet();
  const { enableViaDepositWallet } = useDepositWallet();
  const invalidateUserMe = useInvalidateUserMe();

  // Get trading status from context (single source of truth - avoids duplicate API calls)
  const {
    tradingEnabled: contextTradingEnabled,
    walletLoading: contextLoading,
    setTradingEnabled: setContextTradingEnabled
  } = usePolymarketBalanceContext();

  const [isLoading, setIsLoading] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [needsRetry, setNeedsRetry] = useState(false);
  const [needsSafeUpgrade, setNeedsSafeUpgrade] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<EnableTradingState["step"]>("idle");
  const [stepMessage, setStepMessage] = useState<string | null>(null);

  // When forceBackendCheck is true, we're still checking with the backend
  const [isCheckingBackend, setIsCheckingBackend] = useState(options?.forceBackendCheck ?? false);

  // Track if we've synced with context to avoid overwriting local state after enabling
  const hasSyncedWithContext = useRef(false);
  const backendCheckDone = useRef(false);

  // EVM embedded wallet (Polygon/Polymarket) — always used for signing
  const embeddedWallet = wallets.find(
    (wallet) => wallet.walletClientType === "privy"
  );
  // Solana embedded wallet (DFlow/Kalshi)
  const solanaWallet = solanaWallets[0] ?? null;

  const eoaAddress = embeddedWallet?.address ?? null;
  const [safeAddress, setSafeAddress] = useState<string | null>(null);
  const [depositWalletAddress, setDepositWalletAddress] = useState<string | null>(null);
  /**
   * Custody discriminator surfaced by /api/trading/status. NULL until the
   * status check completes; matches `users.wallet_type` on the backend.
   * Existing Safe users always come back as 'safe' so the enable flow keeps
   * routing them through `enableViaSafe`, even when the deposit-wallet flag
   * is on for new signups.
   */
  const [walletType, setWalletType] = useState<'safe' | 'deposit' | null>(null);
  // Trading wallet (display): deposit_wallet → Safe → EOA. Same precedence
  // as the backend resolver so the user always sees the address that holds
  // their balance, regardless of custody model.
  const walletAddress = depositWalletAddress || safeAddress || eoaAddress;

  /**
   * Verificar si el usuario ya tiene trading habilitado
   * Tambien detecta estado incompleto (tiene wallet pero no credenciales)
   * IMPORTANTE: También actualiza el contexto para mantener consistencia
   */
  const checkTradingStatus = useCallback(async (): Promise<boolean> => {
    if (!authenticated) return false;

    try {
      // Get access token for MiniApp (cookies may not work in Telegram or Chrome mobile)
      // Retry once if first attempt fails (helps with slow token refresh)
      let accessToken: string | null = null;
      try {
        accessToken = await getAccessToken();
        if (!accessToken) {
          await new Promise(resolve => setTimeout(resolve, 500));
          accessToken = await getAccessToken();
        }
      } catch {
        // Retry once after delay
        await new Promise(resolve => setTimeout(resolve, 500));
        accessToken = await getAccessToken();
      }
      const headers: Record<string, string> = {};
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }

      const { ok, data } = await getCachedTradingStatus(headers);
      if (ok && data) {
        // Legacy-EOA upgrade flag: trading enabled but neither a Safe nor a
        // deposit wallet on file. New "deposit"-flow users count as upgraded.
        const upgradeNeeded =
          data.tradingEnabled === true && !data.safeAddress && !data.depositWalletAddress;
        setNeedsSafeUpgrade(upgradeNeeded);
        // Store wallet info for display (trading wallet, not EOA).
        if (data.safeAddress) setSafeAddress(data.safeAddress);
        if (data.depositWalletAddress) setDepositWalletAddress(data.depositWalletAddress);
        if (data.walletType) setWalletType(data.walletType as 'safe' | 'deposit');
        // If upgrade needed, report as NOT enabled so all UIs show the enable/upgrade flow.
        setIsEnabled(upgradeNeeded ? false : data.tradingEnabled);
        setContextTradingEnabled(upgradeNeeded ? false : data.tradingEnabled);
        // Detectar estado incompleto: tiene wallet pero no credenciales.
        setNeedsRetry(data.needsRetry === true);
        return upgradeNeeded ? false : data.tradingEnabled;
      }
      return false;
    } catch (err) {
      // Status check failed - assume not enabled
      return false;
    }
  }, [authenticated, setContextTradingEnabled]);

  // When forceBackendCheck is true, check with backend before trusting context
  // Note: We set isCheckingBackend=true here because SSR initializes it as false
  useEffect(() => {
    if (options?.forceBackendCheck && !backendCheckDone.current) {
      // First, immediately set checking to true (SSR may have initialized it as false)
      setIsCheckingBackend(true);

      // Only proceed with check if authenticated
      if (authenticated) {
        backendCheckDone.current = true;
        // Don't sync with context until we've checked with backend
        checkTradingStatus().then(() => {
          setIsCheckingBackend(false);
        }).catch(() => {
          setIsCheckingBackend(false);
        });
      }
    }
  }, [options?.forceBackendCheck, authenticated, checkTradingStatus]);

  // Sync with context value (no API call - context already has the data)
  // Only sync once when context loads, don't override if we enabled locally
  // Skip sync if forceBackendCheck is true (we'll use backend result instead)
  useEffect(() => {
    if (!contextLoading && !hasSyncedWithContext.current && !options?.forceBackendCheck) {
      setIsEnabled(contextTradingEnabled);
      hasSyncedWithContext.current = true;
    }
  }, [contextLoading, contextTradingEnabled, options?.forceBackendCheck]);

  /**
   * Reset estado
   */
  const reset = useCallback(() => {
    setError(null);
    setStep("idle");
    setStepMessage(null);
  }, []);

  /**
   * Habilitar trading - flujo via Gnosis Safe
   *
   * 1. Create embedded wallet if needed (Privy creates on login, but just in case)
   * 2. Register session signer (for server-side copytrading)
   * 3. Deploy Safe + batch approvals via Polymarket relayer (gasless)
   * 4. Create CLOB API credentials (signatureType=2)
   * 5. Send safeAddress + credentials to backend
   */
  const enableTrading = useCallback(async (): Promise<{
    success: boolean;
    error?: string;
    depositWalletAddress?: string;
    safeAddress?: string;
  }> => {
    if (!ready || !authenticated) {
      return { success: false, error: "Not authenticated" };
    }

    setIsLoading(true);
    setError(null);

    try {
      // Step 1: Ensure we have an embedded wallet
      let currentWalletAddress = embeddedWallet?.address || null;

      if (!currentWalletAddress) {
        setStep("creating_wallet");
        setStepMessage("Creating wallet...");

        try {
          const newWallet = await createWallet();
          currentWalletAddress = newWallet.address;
        } catch (createError: unknown) {
          setIsLoading(false);
          setStep("error");
          return { success: false, error: "Could not create trading wallet" };
        }
      }

      if (!currentWalletAddress) {
        setStep("error");
        return { success: false, error: "Failed to create trading wallet" };
      }

      // Step 2: Add server authorization key as session signer
      // This allows our backend to sign transactions on behalf of the user (copytrading)
      setStep("registering_signer");
      setStepMessage("Authorizing server for trading...");

      cachedConfig = null;

      let privyConfig: PrivyConfig;
      try {
        privyConfig = await getPrivyConfig();
      } catch (configError: unknown) {
        setError("Failed to get trading configuration. Please try again.");
        setStep("error");
        setIsLoading(false);
        return { success: false, error: "Failed to get trading configuration" };
      }

      // Remove existing session signers to ensure latest policy
      try {
        await removeSigners({ address: currentWalletAddress });
      } catch (removeError) {
        // Ignore
      }
      if (solanaWallet?.address) {
        try {
          await removeSigners({ address: solanaWallet.address });
        } catch (removeError) {
          // Ignore
        }
      }

      // Register session signer for EVM wallet (unified policy: Polygon + BSC + LI.FI)
      try {
        await addSigners({
          address: currentWalletAddress,
          signers: [
            {
              signerId: privyConfig.authorizationKeyId,
              policyIds: [privyConfig.policyId],
            },
          ],
        });
      } catch (signerError: unknown) {
        const errorMessage = signerError instanceof Error ? signerError.message : String(signerError);
        let userError = "Failed to authorize trading. ";
        if (errorMessage.includes("rejected") || errorMessage.includes("denied")) {
          userError += "Please approve the authorization request.";
        } else if (errorMessage.includes("network") || errorMessage.includes("timeout")) {
          userError += "Network error - please check your connection and try again.";
        } else {
          userError += "Please try again or contact support if the problem persists.";
        }
        setError(userError);
        setStep("error");
        setIsLoading(false);
        return { success: false, error: userError };
      }

      // Register session signer for Solana wallet — non-blocking
      if (solanaWallet?.address && privyConfig.solanaPolicyId) {
        try {
          await addSigners({
            address: solanaWallet.address,
            signers: [
              {
                signerId: privyConfig.authorizationKeyId,
                policyIds: [privyConfig.solanaPolicyId],
              },
            ],
          });
        } catch (solanaSignerError: unknown) {
          // Non-blocking
        }
      }

      // Step 3: Deploy wallet + approvals + CLOB credentials via relayer (gasless).
      //
      // Branch matrix (must hold even after multiple Enable presses):
      //   - User already has a Safe (walletType==='safe' OR safeAddress set)
      //     → KEEP the Safe path regardless of USE_DEPOSIT_WALLET. Migrating
      //       a live Safe user mid-flight would orphan their pUSD/positions.
      //   - User already has a deposit wallet (walletType==='deposit')
      //     → KEEP the deposit-wallet path. Re-running enable is idempotent.
      //   - Brand-new user (no walletType, no safeAddress, no
      //     depositWalletAddress) → honor USE_DEPOSIT_WALLET to pick the
      //     starting custody model.
      const useDepositWalletForThisUser =
        walletType === 'deposit' ||
        (USE_DEPOSIT_WALLET && walletType !== 'safe' && !safeAddress);

      setStep("enabling");

      let enableBody: Record<string, unknown>;
      let createdDepositAddress: string | undefined;
      let createdSafeAddress: string | undefined;
      if (useDepositWalletForThisUser) {
        setStepMessage("Setting up deposit wallet...");
        const { eoaAddress, depositWalletAddress, credentials } =
          await enableViaDepositWallet((msg) => {
            setStepMessage(msg);
          });
        // Persist on the hook so consumers (modal address display, balance
        // context fallback, etc.) see the new wallet immediately — without
        // this the hook stays at eoaAddress until the next /api/trading/status
        // refetch and the user sees their EOA as the "deposit address",
        // which is fundamentally wrong (funds sent there are stranded).
        setDepositWalletAddress(depositWalletAddress);
        createdDepositAddress = depositWalletAddress;
        enableBody = {
          eoaAddress,
          depositWalletAddress,
          credentials: {
            apiKey: credentials.apiKey,
            secret: credentials.secret,
            passphrase: credentials.passphrase,
          },
        };
      } else {
        setStepMessage("Setting up Safe wallet...");
        const { eoaAddress, safeAddress, credentials } = await enableViaSafe((msg) => {
          setStepMessage(msg);
        });
        setSafeAddress(safeAddress);
        enableBody = {
          eoaAddress,
          safeAddress,
          credentials: {
            apiKey: credentials.apiKey,
            secret: credentials.secret,
            passphrase: credentials.passphrase,
          },
        };
      }

      // Step 4: Send wallet info + credentials to backend.
      setStepMessage("Saving trading configuration...");

      const accessToken = await getAccessToken();
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }

      const enableResponse = await fetch("/api/trading/enable", {
        method: "POST",
        headers,
        body: JSON.stringify(enableBody),
      });

      const contentType = enableResponse.headers.get("content-type");
      let enableResult: any;
      if (contentType && contentType.includes("application/json")) {
        enableResult = await enableResponse.json();
      } else {
        throw new Error(
          enableResponse.status === 504
            ? "Setup is taking longer than expected. Please try again."
            : "Server error — please try again."
        );
      }

      if (!enableResponse.ok) {
        throw new Error(enableResult.error || "Failed to enable trading");
      }

      setIsEnabled(true);
      setContextTradingEnabled(true);
      invalidateTradingStatusCache();
      // Invalidate /api/users/me so PolymarketBalanceContext picks up the
      // freshly-persisted deposit_wallet_address / safe_address — without
      // this the wallet tab in /settings keeps showing "Enable trading"
      // because polymarketTradingAddress = depositWallet || safe stays
      // null on the cached UserMe payload.
      invalidateUserMe();
      setNeedsRetry(false);
      setNeedsSafeUpgrade(false);
      setStep("done");
      setStepMessage("Trading enabled!");

      return {
        success: true,
        depositWalletAddress: createdDepositAddress,
        safeAddress: createdSafeAddress,
      };
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : "Failed to enable trading";
      // Report client-side enable failures to the backend so they're visible
      // in logs (Loki: domain="enable-error"). These happen BEFORE
      // /api/trading/enable is reached (e.g. the deposit-wallet SDK derivation
      // throwing on an RPC error), so without this the backend is blind to a
      // whole class of onboarding breakage. Fire-and-forget; never block UI.
      try {
        fetch("/api/telemetry/enable-error", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            // `step` state isn't in this callback's dep array, so its closure
            // value would be stale — send the error message (which already
            // encodes the failing stage) and the fresh walletType instead.
            message: errorMessage,
            walletType: walletType ?? null,
          }),
          keepalive: true,
        }).catch(() => {});
      } catch {
        // ignore — telemetry must never break the enable flow
      }
      setError(errorMessage);
      setStep("error");
      setStepMessage(null);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, [ready, authenticated, embeddedWallet, solanaWallet, createWallet, addSigners, removeSigners, enableViaSafe, enableViaDepositWallet, setContextTradingEnabled, walletType, safeAddress]);

  return {
    isLoading,
    isEnabled,
    isCheckingBackend,
    needsRetry,
    needsSafeUpgrade,
    error,
    step,
    stepMessage,
    walletAddress,
    enableTrading,
    checkTradingStatus,
    reset,
  };
}
